/**
 * User Notifications API Service
 *
 * Wraps the /me/notifications REST surface. Response interceptor unwraps the
 * { success, data } envelope, so `response.data` already holds the inner
 * payload.
 */

import { apiClient } from '@/api/config';
import type {
  ListUserNotificationsParams,
  ListUserNotificationsResponse,
  UnreadCountResponse,
  UserNotification,
} from '../types/user-notifications.types';

const ENDPOINTS = {
  LIST: '/me/notifications',
  UNREAD_COUNT: '/me/notifications/unread-count',
  MARK_READ: (id: string) => `/me/notifications/${id}/read`,
  MARK_ALL_READ: '/me/notifications/read-all',
} as const;

export const listUserNotifications = async (
  params?: ListUserNotificationsParams
): Promise<ListUserNotificationsResponse> => {
  const search = new URLSearchParams();
  if (params?.limit) search.append('limit', String(params.limit));
  if (params?.cursor) search.append('cursor', params.cursor);
  if (params?.unreadOnly) search.append('unreadOnly', 'true');

  const url = search.toString() ? `${ENDPOINTS.LIST}?${search.toString()}` : ENDPOINTS.LIST;
  const response = await apiClient.get<ListUserNotificationsResponse>(url);
  return (
    response.data ?? {
      items: [] as UserNotification[],
      nextCursor: null,
    }
  );
};

export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<UnreadCountResponse>(ENDPOINTS.UNREAD_COUNT);
  return response.data?.count ?? 0;
};

export const markNotificationRead = async (id: string): Promise<void> => {
  await apiClient.post(ENDPOINTS.MARK_READ(id));
};

export const markAllNotificationsRead = async (): Promise<{ count: number }> => {
  const response = await apiClient.post<{ count: number }>(ENDPOINTS.MARK_ALL_READ);
  return response.data ?? { count: 0 };
};
